<template>
  <div class="alert-banner">
    <img class="alert-banner__icon" src="@/assets/icons/alarm_add.svg" :alt="iconAlt" />
    <MarkdownRenderer v-if="content" :content="content" tag="span" class="small alert-banner__note" />
    <div v-else class="alert-banner__copy">
      <span class="bold small">{{ title }}</span>
      <span v-if="description" class="small">{{ description }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MarkdownRenderer from "@/components/MarkdownRenderer.vue";

/**
 * Inline alert used on the payment instructions page (icon + copy on a tinted
 * background). Supports two content shapes:
 *   - `title` + `description` for structured, plain-text notes.
 *   - `content` for a raw markdown/HTML string (e.g. provider warnings), which
 *     takes precedence and is rendered safely via MarkdownRenderer (no v-html).
 */
export default defineComponent({
  name: "AlertBanner",
  components: { MarkdownRenderer },
  props: {
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    // Raw markdown/HTML note. When set, it is rendered instead of title/description.
    content: {
      type: String,
      default: "",
    },
    iconAlt: {
      type: String,
      default: "",
    },
  },
});
</script>

<style scoped>
.alert-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fbe5be;
  border-radius: 16px;
  padding: 16px 8px;
  margin: 0 16px 12px;
}

.alert-banner__icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.alert-banner__copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Sizing comes from the global `.small`; the leading <strong> acts as the title. */
.alert-banner__note :deep(strong) {
  display: block;
  margin-bottom: 4px;
  font-weight: 590;
}
</style>
